// For V3 gates: https://product.hubteam.com/docs/gating/next-gen/usage.html#frontend-usage
import { userInfoSync } from 'hub-http/userInfo';
import Raven from 'raven-js';
import memoize from 'transmute/memoize';
import { isSome } from 'utility-belt/typeguards';
import { getSearch } from 'utility-belt/globalWrappers';
import { getURLParameter, isQA } from 'utility-belt/urls';
import { LocalStorage } from './browserStorage';
export function createAuthStack(authGetter) {
  const getAuth = () => {
    try {
      return authGetter();
    } catch (err) {
      Raven.captureException(err, {
        extra: {
          description: 'Failed to retrieve authorization synchronously. This is not expected.'
        }
      });
      return {
        gates: [],
        user: null,
        portal: null
      };
    }
  };
  const useAuth = () => getAuth();

  /** @note v3 gates are prefixed with 'g8:' */
  const getOverride = key => {
    if (!isQA()) {
      return null;
    }
    const urlParam = getURLParameter(key);
    if (isSome(urlParam)) {
      return urlParam === 'true';
    }
    const storedValue = LocalStorage.get(key);
    if (typeof storedValue === 'boolean') {
      return storedValue;
    }
    return null;
  };
  const hasGate = memoize(gate => {
    const {
      gates
    } = getAuth();
    const override = getOverride(gate);
    if (isSome(override)) {
      // eslint-disable-next-line no-console
      console.log(`[DEV] Overriding gate "${gate}" with value ${override}`);
      return override;
    }
    return (gates !== null && gates !== void 0 ? gates : []).includes(gate);
  }, gate => `gate: ${gate}, searchParams: ${getSearch()}`);
  const hasScope = memoize(scope => {
    var _user$scopes;
    const {
      user
    } = getAuth();
    const override = getOverride(scope);
    if (isSome(override)) {
      // eslint-disable-next-line no-console
      console.log(`[DEV] Overriding scope "${scope}" with value ${override}`);
      return override;
    }
    return ((_user$scopes = user === null || user === void 0 ? void 0 : user.scopes) !== null && _user$scopes !== void 0 ? _user$scopes : []).includes(scope);
  }, scope => `scope: ${scope}, searchParams: ${getSearch()}`);
  return {
    getAuth,
    useAuth,
    hasGate,
    hasScope,
    getOverride
  };
}
export const {
  getAuth,
  useAuth,
  hasGate,
  hasScope
} = createAuthStack(userInfoSync);
export const COMMERCE_CONTRACT_SCOPES = Object.freeze({
  /** Delete access to the CONTRACT CrmObject https://tools.hubteamqa.com/launch/scopes/5756/parcels */
  delete: 'contracts-delete',
  /** Read access to the CONTRACT CrmObject https://tools.hubteamqa.com/launch/scopes/5754/parcels */
  read: 'contracts-read',
  /** Write access to the CONTRACT CrmObject https://tools.hubteamqa.com/launch/scopes/5755/parcels */
  write: 'contracts-write',
  /** Access to the CONTRACT CrmObject https://tools.hubteamqa.com/launch/scopes/5753/parcels */
  access: 'contracts-access'
});
export const SCOPES = Object.freeze({
  contract: COMMERCE_CONTRACT_SCOPES,
  /** Used to lightly indicate JITA users https://tools.hubteamqa.com/launch/scopes/1399/parcels */
  jitaUser: 'jita-user',
  /** Provides the ability to add/edit/remove associations and labels https://tools.hubteamqa.com/launch/scopes/5652/parcels */
  writeCRMAssociations: 'crm-associations-write-access'
});